// src/context/userContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "react-oidc-context";
import useLogout from "../hooks/authentication_hooks/useLogout";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const auth = useAuth();
  const { handleLogout } = useLogout();

  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [frozenChildren, setFrozenChildren] = useState(children);
  const wasAuthenticated = useRef(false);
  const logoutTimer = useRef(null);

  const isSilent = auth.activeNavigator === "signinSilent";

  /* ---------------------------------------------------------------------- */
  /* 🧠 Manage user state                                                   */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (isSilent) return;

    if (auth.user && !auth.user?.expired) {
      setUser((prev) =>
        prev?.access_token === auth.user?.access_token ? prev : auth.user,
      );
      setHydrated(true);
      wasAuthenticated.current = true;
    } else if (!auth.isLoading) {
      setUser(null);
      setHydrated(true);
    }
  }, [auth.user, auth.isLoading, isSilent]);

  /* ---------------------------------------------------------------------- */
  /* 🚨 Detect token loss → logout                                          */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (isSilent || auth.isLoading) return;

    const token = auth.user?.access_token ?? null;
    const expiresAt = auth.user?.expires_at ?? null;
    const alreadyLoggingOut =
      sessionStorage.getItem("logoutInProgress") === "1";

    console.log("🧠 UserProvider token check —", {
      token: !!token,
      wasAuthenticated: wasAuthenticated.current,
      alreadyLoggingOut,
      expired: auth.user?.expired,
      isLoading: auth.isLoading,
      isSilent,
    });

    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }

    if (wasAuthenticated.current && !token && !alreadyLoggingOut) {
      console.log("🚨 Logging out — token lost");
      sessionStorage.setItem("logoutInProgress", "1");
      wasAuthenticated.current = false;
      handleLogout();
      return;
    }

    if (expiresAt && !alreadyLoggingOut) {
      const expiresInMs = expiresAt * 1000 - Date.now();
      if (expiresInMs > 0) {
        logoutTimer.current = setTimeout(() => {
          sessionStorage.setItem("logoutInProgress", "1");
          wasAuthenticated.current = false;
          handleLogout();
        }, expiresInMs + 2000);
      }
    }

    if (!auth.isAuthenticated && !token) {
      wasAuthenticated.current = false;
    }

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [auth.user, auth.isAuthenticated, auth.isLoading, isSilent, handleLogout]);

  /* ---------------------------------------------------------------------- */
  /* 🧩 Global unauthorized handler for API 401/403                         */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    const handleGlobalApiError = (e) => {
      const status = e.detail?.status;
      const alreadyLoggingOut =
        sessionStorage.getItem("logoutInProgress") === "1";

      if ((status === 401 || status === 403) && !alreadyLoggingOut) {
        sessionStorage.setItem("logoutInProgress", "1");
        wasAuthenticated.current = false;
        handleLogout();
      }
    };

    window.addEventListener("apiError", handleGlobalApiError);
    return () => window.removeEventListener("apiError", handleGlobalApiError);
  }, [handleLogout]);

  /* ---------------------------------------------------------------------- */
  /* 🧊 Freeze UI during silent renew                                       */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!isSilent) setFrozenChildren(children);
  }, [children, isSilent]);

  /* ---------------------------------------------------------------------- */
  /* 🧩 Multi-tab logout broadcast                                           */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStorage = (e) => {
      if (e.key === "authState" && e.newValue === "signOut") {
        const alreadyLoggingOut =
          sessionStorage.getItem("logoutInProgress") === "1";
        if (!alreadyLoggingOut) {
          sessionStorage.setItem("logoutInProgress", "1");
          handleLogout();
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [handleLogout]);

  /* ---------------------------------------------------------------------- */
  /* ✅ Memoized context value                                              */
  /* ---------------------------------------------------------------------- */
  const value = useMemo(
    () => ({
      user,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      isSilent,
      hydrated,
      accessToken: user?.access_token || null,
    }),
    [user, auth.isAuthenticated, auth.isLoading, isSilent, hydrated],
  );

  const content = isSilent ? frozenChildren : children;

  return <UserContext.Provider value={value}>{content}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
